/**
 * =================================================================
 *          General Utility Functions for the Web Application
 * =================================================================
 *
 * This file contains a collection of helper functions used across
 * the entire frontend application. They are designed to be pure,
 * reusable, and production-ready.
 *
 * Sections:
 * 1. ClassName Merging
 * 2. Date & Time Formatting
 * 3. File & Data Formatting
 * 4. Text Processing
 * 5. Error Handling
 * 6. Form & Validation
 * 7. Local Storage
 * 8. URL & String Manipulation
 * 9. Image Handling
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Project } from "../types";

// --- 1. ClassName Merging ---

/**
 * A utility function to conditionally join class names together.
 * It merges Tailwind CSS classes without style conflicts.
 * @param {...ClassValue[]} inputs - A list of class names or conditions.
 * @returns {string} The merged, conflict-free class name string.
 * @example cn("p-4", "font-bold", { "bg-red-500": hasError })
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// --- 1b. Project Display Helper ---

/**
 * Returns a human-readable display name for a project.
 * Prefers owner_name, falls back to address, then a default.
 *
 * @param {Project} project - The project to get a display name for.
 * @returns {string} The display name.
 */
export function getProjectDisplayName(project: Project): string {
  if (project.owner_name) return project.owner_name;
  return project.address || 'Untitled Project';
}

// --- 2. Date & Time Formatting ---

/**
 * Formats a date into a user-friendly string (e.g., "April 15, 2024").
 * @param {string | number | Date} date - The date to format.
 * @param {Intl.DateTimeFormatOptions} [options] - Custom formatting options.
 * @returns {string} The formatted date string.
 */
export function formatDate(
  date: string | number | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  try {
    return new Intl.DateTimeFormat("en-US", { ...defaultOptions, ...options }).format(new Date(date));
  } catch (error) {
    console.error("Invalid date provided to formatDate:", date);
    return "Invalid Date";
  }
}

/**
 * Formats a date into a relative time string (e.g., "2 days ago").
 * @param {string | number | Date} date - The date to format.
 * @returns {string} The relative time string.
 */
export function formatRelativeTime(date: string | number | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.round((now.getTime() - then.getTime()) / 1000);

  const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];
  const units: Intl.RelativeTimeFormatUnit[] = ["second", "minute", "hour", "day", "week", "month", "year"];

  const unitIndex = cutoffs.findIndex(cutoff => diffInSeconds < cutoff);
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
  const value = -Math.floor(diffInSeconds / divisor);

  try {
    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    return rtf.format(value, units[unitIndex]);
  } catch (error) {
    console.error("Invalid date provided to formatRelativeTime:", date);
    return "Invalid Date";
  }
}

// --- 3. File & Data Formatting ---

/**
 * Formats a number of bytes into a human-readable string (e.g., "1.2 MB").
 * @param {number} bytes - The number of bytes.
 * @param {number} [decimals=2] - The number of decimal places to use.
 * @returns {string} The formatted file size string.
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

// --- 4. Text Processing ---

/**
 * Truncates a string to a specified length and adds an ellipsis.
 * @param {string} text - The text to truncate.
 * @param {number} length - The maximum length of the string.
 * @returns {string} The truncated string.
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) {
    return text;
  }
  return text.substring(0, length) + "...";
}

/**
 * Capitalizes the first letter of a string.
 * @param {string} str - The string to capitalize.
 * @returns {string} The capitalized string.
 */
export function capitalize(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// --- 5. Error Handling ---

/**
 * Extracts a user-friendly error message from various error types.
 * @param {*} error - The error object.
 * @returns {string} A human-readable error message.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return "An unknown error occurred.";
}

// --- 6. Form & Validation ---

/**
 * Checks if a value is a valid email address.
 * @param {string} email - The email string to validate.
 * @returns {boolean} True if the email is valid, false otherwise.
 */
export function isEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Checks if a value is not empty (after trimming whitespace).
 * @param {string} value - The string to check.
 * @returns {boolean} True if the value is not empty, false otherwise.
 */
export function isNotEmpty(value: string): boolean {
  return value.trim().length > 0;
}

// --- 7. Local Storage ---

/**
 * Safely gets an item from local storage and parses it as JSON.
 * @param {string} key - The key of the item to retrieve.
 * @returns {T | null} The parsed item, or null if not found or an error occurs.
 */
export function getFromLocalStorage<T>(key: string): T | null {
  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  } catch (error) {
    console.warn(`Error reading from localStorage key “${key}”:`, error);
    return null;
  }
}

/**
 * Safely sets an item in local storage, converting it to a JSON string.
 * @param {string} key - The key of the item to set.
 * @param {T} value - The value to set.
 */
export function setInLocalStorage<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Error writing to localStorage key “${key}”:`, error);
  }
}

// --- 8. URL & String Manipulation ---

/**
 * Converts a string into a URL-friendly slug.
 * @param {string} str - The string to convert.
 * @returns {string} The slugified string.
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove non-word chars
    .replace(/[\s_-]+/g, "-") // Collapse whitespace and replace with -
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing dashes
}

// --- 9. Image Handling ---

/**
 * Generates a URL for an optimized image.
 * Placeholder function: In a real app, this would integrate with an image CDN.
 * @param {string} originalUrl - The original URL of the image.
 * @param {{ width?: number; height?: number; quality?: number }} options - Optimization options.
 * @returns {string} The URL for the optimized image.
 */
export function getOptimizedImageUrl(
  originalUrl: string,
  options: { width?: number; height?: number; quality?: number } = {}
): string {
  // In a real-world application, you would construct a URL for your image
  // optimization service (e.g., Cloudinary, Imgix, Azure Image Resizer).
  // Example for a hypothetical service:
  // const params = new URLSearchParams();
  // if (options.width) params.append('w', String(options.width));
  // if (options.height) params.append('h', String(options.height));
  // if (options.quality) params.append('q', String(options.quality));
  // return `https://my-cdn.com/img?url=${encodeURIComponent(originalUrl)}&${params.toString()}`;

  // For now, we just return the original URL.
  return originalUrl;
}
